import React, { Component } from "react";
import axios from "axios";
import { Link } from "react-router";
import { FirebaseService, UserNotificationService } from "@services";
import utils from "@utils";
import Modal from "../Modal/Modal";
import Switch from "../Switch/Switch";

import "./NotificationsModal.scss";

const notificationsSupported = FirebaseService.messaging !== null;
const displayCount = 25;
let cancel;
const getCancelToken = c => {
	cancel = c;
};

class NotificationsModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			isOpen: false,
			notifications: [],
			loading: false,
			pushNotificationsEnabled: false,
		};
	}

	/** Retrieves a list of recent notifications for the current user */
	getNotifications = () => {
		const { lang } = global;

		this.setState({ loading: true });

		UserNotificationService.getNotifications({ displayCount }, getCancelToken)
			.then(({ data }) => {
				cancel = undefined;
				this.setState({ loading: false, notifications: data?.ItemList ?? [] });
			})
			.catch(error => {
				if (!axios.isCancel(error)) {
					this.setState({ loading: false });

					const { response } = error;
					utils.notify({
						type: "error",
						message: response?.data?.Message ?? lang.t("Error loading notifications"),
					});
				}
			});
	};

	/**
	 * Marks notifications as having been read by the user already
	 * @param {Array<Number>} ids The ID numbers of notifications
	 * @param {Boolean} single Whether or not a single notification was marked as read. Defaults to `false`.
	 *
	 * @todo Rewrite this method without the parameter `single`. It looks to be unecessary.
	 */
	markAsRead =
		(ids = [], single = false) =>
		() => {
			if (ids.length === 0) return;

			const { lang } = global;

			UserNotificationService.setNotificationsAsRead(ids)
				.then(() => {
					if (single) {
						this.setState(state => ({
							notifications: state.notifications.map(item =>
								ids.includes(item.Id) ? { ...item, Read: true } : item,
							),
						}));
					}
				})
				.catch(error => {
					if (!axios.isCancel(error)) {
						const { response } = error;
						utils.notify({
							type: "error",
							message:
								response?.data?.Message ?? lang.t("Error updating notifications"),
						});
					}
				});
		};

	/** @async Toggles Firebase push notification permissions for the current user on the current device */
	toggleNotifications = async () => {
		const { pushNotificationsEnabled } = this.state;

		if (pushNotificationsEnabled) {
			FirebaseService.removeToken();
			this.setState({ pushNotificationsEnabled: false });
		} else {
			const granted = await FirebaseService.requestPermission();
			this.setState({ pushNotificationsEnabled: granted });
		}
	};

	openModal = () => {
		this.setState({ isOpen: true }, () => {
			this.getNotifications();

			FirebaseService.getNotificationsEnabled().then(notificationsEnabled => {
				this.setState({ pushNotificationsEnabled: notificationsEnabled });
			});
		});
	};

	resetModal = () => {
		const { notifications } = this.state;
		const unReadNotifications = notifications.filter(item => !item.Read).map(item => item.Id);

		if (cancel) cancel();
		this.markAsRead(unReadNotifications)();
		this.setState({
			isOpen: false,
			notifications: [],
			loading: false,
		});
	};

	renderNotification = item => {
		const { android, notification: message } = JSON.parse(item.Message);
		const date = new Date(`${item.CreateDateUtc}+00:00`).getTime();

		return (
			<div
				key={`app_notification_${item.Id}`}
				className="row"
			>
				<div className="col-xs-1 mark-as-read-btn">
					<If condition={!item.Read}>
						<button
							type="button"
							className="btn btn-link"
							onClick={this.markAsRead([item.Id], true)}
							title="Mark as read"
						>
							<i className="rkf rkf-circle" />
						</button>
					</If>
				</div>

				<div className="col-xs-11">
					<Link
						to={`/${android?.notification?.click_action}`}
						onClick={this.resetModal}
					>
						<div>
							<strong>{message.title}</strong>
						</div>
						<div>{message.body}</div>
						<div className="time-since">{utils.timeSince(date)}</div>
					</Link>
				</div>
			</div>
		);
	};

	render() {
		const { lang } = global;
		const { isOpen, notifications, loading, pushNotificationsEnabled } = this.state;

		const unReadNotifications = notifications.filter(item => !item.Read);
		const readNotifications = notifications.filter(item => item.Read);

		return (
			<Modal
				show={isOpen}
				onHide={this.resetModal}
				id="app-notifications-modal"
				size="sm"
			>
				<Modal.Body>
					<div id="app-notifications-header">
						<h4>{lang.t("Notifications")}</h4>
						<If condition={notificationsSupported}>
							<div
								id="app-toggle-notifications"
								title={lang.t("Enable push notifications")}
							>
								<Switch
									onClick={this.toggleNotifications}
									on={pushNotificationsEnabled}
								/>
							</div>
						</If>
					</div>
					<If condition={notificationsSupported && !pushNotificationsEnabled}>
						<small id="app-notifications-toggle-message">
							Enable to receive real-time in-browser notifications for job related
							events.
						</small>
					</If>
					<hr id="app-notifications-header-divider" />
					<div id="app-notifications-body">
						<If condition={unReadNotifications.length > 0}>
							<h4>{lang.t("New")}</h4>
						</If>
						<For
							each="item"
							of={unReadNotifications}
						>
							{this.renderNotification(item)}
						</For>

						<If condition={readNotifications.length > 0}>
							<h4>{lang.t("Earlier")}</h4>
						</If>
						<For
							each="item"
							of={readNotifications}
						>
							{this.renderNotification(item)}
						</For>
					</div>
					<If condition={loading}>
						<div className="text-center">
							<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
						</div>
					</If>
				</Modal.Body>
			</Modal>
		);
	}
}

export default NotificationsModal;
